// ---------------------------------------------------------------------------
// decompressSignal — Utility to decompress CompressedData in a SIGS signal.
//
// Signals from Substrate may have CustomProperties.CompressedData as a
// base64-encoded gzip string. This utility decompresses it in-place so the
// signal can be fully rendered in the debug UI.
// ---------------------------------------------------------------------------

/**
 * Decompress a base64-encoded gzip string using the browser's DecompressionStream API.
 */
async function gunzipBase64(base64: string): Promise<string> {
  const binary = atob(base64);
  const bytes = Uint8Array.from({ length: binary.length }, (_, i) =>
    binary.charCodeAt(i),
  );
  const ds = new DecompressionStream('gzip');
  const writer = ds.writable.getWriter();
  const reader = ds.readable.getReader();

  // Write and read concurrently to avoid backpressure deadlock.
  const chunks: Uint8Array[] = [];
  await Promise.all([
    writer.write(bytes).then(() => writer.close()),
    (async () => {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (value) chunks.push(value);
      }
    })(),
  ]);
  const byteLength = chunks.reduce((acc, c) => acc + c.length, 0);
  const result = new Uint8Array(byteLength);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }
  return new TextDecoder().decode(result);
}

/**
 * Decompresses `CustomProperties.CompressedData` in a signal JSON string
 * if it is a base64-encoded gzip string. Returns the modified JSON string
 * with CompressedData as a parsed object.
 *
 * If no compressed data is found or decompression fails, returns the
 * original string unchanged.
 */
export async function decompressSignalPayload(
  jsonPayload: string,
): Promise<string> {
  try {
    const signal = JSON.parse(jsonPayload) as Record<string, unknown>;
    const customProps = signal.CustomProperties as
      | Record<string, unknown>
      | undefined;

    if (customProps && typeof customProps.CompressedData === 'string') {
      const decompressed = await gunzipBase64(customProps.CompressedData);
      customProps.CompressedData = JSON.parse(decompressed) as unknown;
      return JSON.stringify(signal);
    }
  } catch {
    // Return original if anything fails
  }
  return jsonPayload;
}
